// Manager Approve popup body — renders the shared, config-driven
// FinanceApprovalPanel (type = submissionManager) inside the dynamic popup.
// Admin tags a module's "Approve" action to popupKey "manager-approve".
//
// This backend has NO by-id detail route for submissions/clients/jobs
// (module-data-detail 404s) and its list projections drop the reference IDs,
// so the popup resolves the full picture itself, in-file, from the endpoints
// that DO work:
//   • getFormGroups(submission,id) → recordMeta.{clientId, jobId}
//   • jobs   → getJobDetailView (jobPayDetails = Client Job Rate, jobTitle)
//   • clients   list → clientName (+ vmsFee when the projection exposes it)
//   • employers list → empCompanyName / empCompanyLocation
//   • locations list → taxValue (W2 tax) + name, matched by id OR name
import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Drawer, Modal, Radio, Spin, message } from 'antd';
import FinanceApprovalPanel from '../../../finance/FinanceApprovalPanel.jsx';
import { FINANCE_TYPE_SUBMISSION_MANAGER } from '../../../finance/financeApprovalConfig.js';
import { resolveSubmissionFinance, getReferralOptions, idOf } from '../../../finance/financeApprovalData.js';
import { approveSubmission as approveSubmissionViaVms } from '../../../../services/popupApi';
import {
  getApproveMailDraft,
} from '../../../../services/mailApproveApi';
import { sendBulkMail } from '../../../../services/dropdownApi';
import { getModuleDocuments } from '../../../../services/documentApi';
import { fetchJsonWithAuth } from '../../../../services/authApi';
import { AUTH_URL } from '../../../../services/apiConfig';
import CommonMailComposer, { resolveTemplateText } from '../../CommonMailComposer.jsx';
import { areValidEmails, parseEmails } from '../../mailUtils.js';

function latestArrayEntry(value) {
  return Array.isArray(value) ? value[value.length - 1] : value;
}

function persistedProposedRate(submission = {}) {
  const entry = latestArrayEntry(submission?.proposedRate);
  const value = entry?.candidateProposedRate
    ?? submission?.candidateProposedRate
    ?? submission?.proposedRateValue;
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function persistedJobBillRate(resolved, seed = {}) {
  const job = resolved?.record?.job ?? resolved?.job ?? seed?.job ?? {};
  const pay = latestArrayEntry(job?.jobPayDetails) ?? {};
  const submission = resolved?.record?.submission ?? seed ?? {};
  const budget = latestArrayEntry(submission?.clientBudget) ?? submission?.clientBudget ?? {};

  // FinanceApprovalPanel uses the END/to value as Client Job Rate when the job
  // contains a budget range. Keep the mail value identical to the popup value.
  const value = pay?.clientBudgetEnd
    ?? pay?.clientBudgetStart
    ?? budget?.clientBudgetEnd
    ?? budget?.clientBudgetStart;
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function normalizeRateCurrency(computed = {}) {
  const raw = String(computed?.currencyRaw ?? '').trim().toUpperCase();
  const display = String(computed?.currency ?? '').trim();

  if (display && !/^[A-Z]{3}$/i.test(display)) return display;
  if (raw === 'USD' || display.toUpperCase() === 'USD') return '$';
  if (raw === 'INR' || display.toUpperCase() === 'INR') return '₹';
  if (raw === 'EUR' || display.toUpperCase() === 'EUR') return '€';
  if (raw === 'GBP' || display.toUpperCase() === 'GBP') return '£';
  return display || raw;
}

function normalizeRateUnit(computed = {}) {
  const raw = String(computed?.rateUnit ?? 'hr').trim();
  return raw.replace(/^\/+/, '') || 'hr';
}

function formatManagerMailRate(rate, computed = {}) {
  const number = Number(rate);
  if (!Number.isFinite(number)) return '';
  const currency = normalizeRateCurrency(computed);
  const unit = normalizeRateUnit(computed);
  return `${currency}${number}/${unit}`;
}

function replaceRenderedFinanceValue(value, labelPattern, replacement) {
  if (replacement === undefined || replacement === null || replacement === '') return String(value ?? '');

  let output = String(value ?? '');
  const renderedValue = String(replacement);
  let taggedMatched = false;

  // Replace the COMPLETE value after a bold label exactly once. This removes
  // stale map[...] output as well as old currency/unit fragments returned by
  // /mail-form. The previous implementation also ran the plain-text fallback
  // on the same <strong> label, which caused duplicated values such as 6868.
  const taggedLabel = new RegExp(
    `(<(?:strong|b)[^>]*>\\s*${labelPattern}\\s*:?\\s*</(?:strong|b)>\\s*)([\\s\\S]*?)(?=</(?:li|p|div)>)`,
    'gi',
  );
  output = output.replace(taggedLabel, (_, prefix) => {
    taggedMatched = true;
    return `${prefix}${renderedValue}`;
  });

  // Plain-text fallback is ONLY for templates that do not use <strong>/<b>.
  if (!taggedMatched) {
    const inlineLabel = new RegExp(
      `(${labelPattern}\\s*:?[ \\t]*)([^<\\r\\n]*)`,
      'gi',
    );
    output = output.replace(inlineLabel, (_, prefix) => `${prefix}${renderedValue}`);
  }

  return output;
}

function resolveManagerApproveFinancePlaceholders(value, submission, computed, resolved, seed) {
  const liveProposed = Number(computed?.proposedRate);
  const proposedRate = Number.isFinite(liveProposed)
    ? liveProposed
    : persistedProposedRate(submission);

  const liveBillRate = Number(computed?.clientJobRate);
  const jobBillRate = Number.isFinite(liveBillRate) && liveBillRate > 0
    ? liveBillRate
    : persistedJobBillRate(resolved, seed);

  const values = { proposedRate, jobBillRate };

  // First handle templates where the raw runtime placeholders survive the
  // backend template resolver.
  let output = String(value ?? '').replace(
    /{{\.?((?:proposedRate)|(?:jobBillRate))}}/g,
    (token, key) => {
      const resolvedValue = values[key];
      return resolvedValue === undefined || resolvedValue === null ? token : String(resolvedValue);
    },
  );

  // Then handle the current backend behavior where /mail-form has already
  // replaced those placeholders with 0 using persisted DB data.
  output = replaceRenderedFinanceValue(
    output,
    'Proposed\\s*Rate',
    formatManagerMailRate(proposedRate, computed),
  );
  output = replaceRenderedFinanceValue(
    output,
    '(?:Job\\s*)?Bill\\s*Rate',
    formatManagerMailRate(jobBillRate, computed),
  );

  return output;
}

// firstText — first non-blank value. `??` only falls through on null/undefined,
// so an upstream chain that resolves to '' (see submittedBy below) would stop
// dead on it and never reach the fallbacks that exist for exactly that case.
function firstText(...values) {
  return values
    .map((value) => (value === null || value === undefined ? '' : String(value).trim()))
    .find(Boolean) ?? '';
}

const sameText = (a, b) => Boolean(a) && a.toLowerCase() === firstText(b).toLowerCase();

// resolveSubmittedBy — "Submitted By" must name the PERSON who submitted.
// resolveSubmissionFinance's display.submittedBy is a `||` chain that ends in
// `flat.recruiterName || flat.submitSource`, and neither of those is a
// submitter: submitSource is the submission's SOURCE ("Internal", or a vendor)
// — this very function reads it as `vendor` one line down — and recruiterName
// is the ASSIGNED recruiter. Real submissions store submitSource but no
// recruiterName, so display.submittedBy came back as the source string, which
// is truthy, which meant the correct seed.signatureEmail fallback never ran
// and the popup tagged "Internal" as the submitter.
// So: take the resolved value only when it is not one of those source/vendor
// strings, then fall back to the submitter's own signature email.
function resolveSubmittedBy(seed, display, vendor) {
  const resolved = firstText(display.submittedBy);
  const isSourceLeak = sameText(resolved, vendor)
    || sameText(resolved, seed.submitSource)
    || sameText(resolved, seed.supplierName)
    || sameText(resolved, seed.source)
    || sameText(resolved, seed.candidateSource);
  return firstText(isSourceLeak ? '' : resolved, seed.signatureEmail);
}

// Display identity — resolved values (form-groups + joins) win over the
// clicked row, which on the detail surface is flattened and often stale.
function buildIdentity(seed, display = {}) {
  const vendor = display.employerName ?? seed.supplierName ?? seed.submitSource;
  return {
    client: display.clientName ?? seed.clientName,
    role: display.jobTitle ?? seed.jobTitle ?? seed.designation,
    reqId: display.reqId ?? seed.jobClientRefrenceId ?? seed.mspReqId ?? seed.submissionId,
    name: display.candidateName
      || [seed.firstName, seed.lastName].filter(Boolean).join(' ')
      || seed.candidateFullName,
    location: display.jobLocation ?? seed.city ?? seed.currentLocation ?? seed.jobLocation,
    vendor,
    submittedBy: resolveSubmittedBy(seed, display, vendor),
    submittedAt: display.createdAt ?? seed.createdAt,
    email: display.email ?? seed.email,
    employerLocation: display.employerLocation,
    employerName: display.employerName,
  };
}

function scalarObjectId(value) {
  const raw = value?.$oid ?? value?._id?.$oid ?? value?._id ?? value?.id?.$oid ?? value?.id ?? value;
  if (raw === null || raw === undefined || typeof raw === 'object') return '';
  const text = String(raw).trim();
  return /^[a-f\d]{24}$/i.test(text) ? text : '';
}

function resolveJobId(...values) {
  for (const value of values) {
    const id = [
      value?.jobId,
      value?.JobId,
      value?.job_id,
      value?.jobDetails?._id,
      value?.jobDetails?.id,
      value?.job?._id,
      value?.job?.id,
      value?.submission?.jobId,
      value?.submission?.JobId,
      value?.submission?.job_id,
      value?.submission?.jobDetails?._id,
      value?.submission?.jobDetails?.id,
      value?.submission?.job?._id,
      value?.submission?.job?.id,
      value?.recordMeta?.jobId,
      value?.recordMeta?.JobId,
      value?.recordMeta?.job_id,
      value?.submission?.recordMeta?.jobId,
      value?.submission?.recordMeta?.JobId,
      value?.submission?.recordMeta?.job_id,
    ].map(scalarObjectId).find(Boolean);
    if (id) return id;
  }
  return '';
}

// referralPayload — the fields ApproveSubmissionHandler (Be_Auth_DevOps)
// reads to push a new submissions.referralDetails entry. Omitted entirely
// when no referral was attached, so a plain rate-only approve never touches
// that array.
function referralPayload(referral) {
  if (!referral?.referralId) return {};

  // The submission approve API is consumed by more than one backend version.
  // Persist the manager-entered referral snapshot using both the canonical and
  // legacy aliases, and also send the normalized object used by the newer
  // submission handler to append submissions.referralDetails[].
  //
  // IMPORTANT: use the referral passed by FinanceApprovalPanel here. Its
  // referralRate is computed from the CURRENT edit state, so a manager changing
  // 10 -> 15 must persist 15 rather than the referral master's old rate.
  const referralRate = Number(referral.referralRate ?? referral.referalRate ?? 0) || 0;
  const referralType = String(
    referral.referralType ?? referral.referalType ?? 'RECURRING',
  ).trim() || 'RECURRING';
  const referralId = referral.referralId ?? referral.referalId;

  return {
    referralId,
    referalId: referralId,
    referralRate,
    referalRate: referralRate,
    referralType,
    referalType: referralType,
    referralEnabled: true,
    referalEnabled: true,
    isReferralApplicable: true,
    referralDetails: {
      referralId,
      referalId: referralId,
      referralRate,
      referalRate: referralRate,
      referralType,
      referalType: referralType,
      referralEnabled: true,
      referalEnabled: true,
      isReferralApplicable: true,
    },
  };
}

function stripDocumentPlaceholderArtifacts(value = '') {
  // Legacy mail-template interpolation stringifies stored file maps as e.g.
  // "map[resumeLocation: ... resumeUniqueName: ... resumeUploadName: ...]".
  // A document placeholder represents an ATTACHMENT, not visible mail text.
  // Remove only map fragments that clearly describe file/document metadata.
  return String(value ?? '')
    .replace(/map\[[^\]]*(?:resume|document|attachment|file)[^\]]*\]/gi, '')
    .replace(/<p>\s*<\/p>/gi, '')
    .replace(/(?:<br\s*\/?>\s*){3,}/gi, '<br><br>')
    .trim();
}

function candidateIdFrom(...values) {
  for (const value of values) {
    const candidateId = [
      value?.candidateId,
      value?.applicantId,
      value?.candidate?._id,
      value?.candidate?.id,
      value?.submission?.candidateId,
      value?.submission?.applicantId,
      value?.recordMeta?.candidateId,
      value?.recordMeta?.applicantId,
    ].map(scalarObjectId).find(Boolean);
    if (candidateId) return candidateId;
  }
  return '';
}

async function documentToFile(document) {
  const url = document?.fileUrl ?? document?.downloadUrl ?? document?.url;
  if (!url) return null;
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Unable to load ${document?.fileName || 'resume'}`);
  const blob = await response.blob();
  const name = document?.fileName
    ?? document?.uploadedFileName
    ?? document?.originalName
    ?? document?.uniqueName
    ?? 'resume';
  return new File([blob], name, {
    type: document?.contentType || blob.type || 'application/octet-stream',
  });
}

async function resolveResumeAttachments(submissionId, resolved, seed) {
  // New uploads normally live in the common documents registry against the
  // candidate record. Check the submission too for older/migrated data.
  const documentRows = [];
  const submissionDocs = await getModuleDocuments('submissions', submissionId, 'resume').catch(() => ({ data: [] }));
  documentRows.push(...(submissionDocs?.data ?? []));

  let candidateId = candidateIdFrom(resolved?.record?.submission, seed, resolved?.record);
  if (!candidateId) {
    // The list-view projection can omit candidateId/applicantId. Read the raw
    // submission only to recover that linkage; this does not alter the popup's
    // existing finance-resolution flow.
    const rawJson = await fetchJsonWithAuth(
      AUTH_URL,
      `/module/list?module=submissions&id=${encodeURIComponent(submissionId)}`,
    ).catch(() => ({}));
    const payload = rawJson?.data ?? rawJson ?? {};
    const rows = Array.isArray(payload?.data) ? payload.data : (Array.isArray(payload) ? payload : []);
    candidateId = candidateIdFrom(rows[0]);
  }

  if (candidateId) {
    const candidateDocs = await getModuleDocuments('candidates', candidateId, 'resume').catch(() => ({ data: [] }));
    documentRows.push(...(candidateDocs?.data ?? []));
  }

  const files = (await Promise.all(documentRows.map(documentToFile))).filter(Boolean);

  // Most candidates have one current resume. De-duplicate in case the registry
  // returns the same file more than once.
  const seen = new Set();
  return files.filter((file) => {
    const key = `${file.name}:${file.size}:${file.type}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function normalizeDraft(draft = {}) {
  return {
    to: (draft.to ?? draft.To ?? []).map((item) => item.email ?? item.Email ?? item).filter(Boolean).join(', '),
    cc: (draft.cc ?? draft.Cc ?? []).map((item) => item.email ?? item.Email ?? item).filter(Boolean).join(', '),
    bcc: (draft.bcc ?? draft.Bcc ?? []).map((item) => item.email ?? item.Email ?? item).filter(Boolean).join(', '),
    subject: draft.subject ?? draft.Subject ?? '',
    contents: stripDocumentPlaceholderArtifacts(draft.content?.value ?? draft.Content?.Value ?? draft.contents ?? draft.body ?? ''),
  };
}

export default function ManagerApproveBody({ context, selectedIds, selectedRecords, moduleName, onClose }) {
  const seed = selectedRecords?.[0] ?? {};
  const submissionId = selectedIds?.[0] ?? idOf(seed);
  const [resolved, setResolved] = useState(null);
  const [loading, setLoading] = useState(true);
  const [referralOptions, setReferralOptions] = useState([]);
  const [approvalChoiceOpen, setApprovalChoiceOpen] = useState(false);
  const [approvalMode, setApprovalMode] = useState('vms');
  const [pendingComputed, setPendingComputed] = useState(null);
  const [pendingReferral, setPendingReferral] = useState(null);
  const [approvalSubmitting, setApprovalSubmitting] = useState(false);
  const [mailDrawerOpen, setMailDrawerOpen] = useState(false);
  const [mailLoading, setMailLoading] = useState(false);
  const [mailSending, setMailSending] = useState(false);
  const [mail, setMail] = useState({
    to: '', cc: '', bcc: '', subject: '', contents: '', attachments: [], showCc: false, showBcc: false,
  });

  useEffect(() => {
    let cancelled = false;
    resolveSubmissionFinance(submissionId, seed)
      .then((data) => { if (!cancelled) setResolved(data); })
      .catch((error) => {
        if (cancelled) return;
        message.error(error?.message ?? 'Unable to load finance details');
        setResolved({ record: { submission: seed }, lookups: {}, display: {} });
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionId]);

  // The "Add Referral" section (submissionManager stage now allows Delivery
  // Manager to attach one here too) needs the referral list — was never
  // fetched by this popup, so the dropdown rendered "No data".
  useEffect(() => {
    let cancelled = false;
    getReferralOptions()
      .then((options) => { if (!cancelled) setReferralOptions(options); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const submitApproval = async () => {
    if (!pendingComputed) return;
    if (approvalMode === 'mail') {
      setApprovalChoiceOpen(false);
      setMailDrawerOpen(true);
      setMailLoading(true);
      try {
        const [draft, attachments] = await Promise.all([
          getApproveMailDraft(submissionId).then(normalizeDraft),
          resolveResumeAttachments(submissionId, resolved, seed).catch((error) => {
            console.warn('Unable to resolve resume attachment:', error);
            return [];
          }),
        ]);
        const submissionRecord = resolved?.record?.submission ?? seed;
        setMail({
          to: resolveManagerApproveFinancePlaceholders(draft.to, submissionRecord, pendingComputed, resolved, seed),
          cc: resolveManagerApproveFinancePlaceholders(draft.cc, submissionRecord, pendingComputed, resolved, seed),
          bcc: resolveManagerApproveFinancePlaceholders(draft.bcc, submissionRecord, pendingComputed, resolved, seed),
          subject: resolveManagerApproveFinancePlaceholders(draft.subject, submissionRecord, pendingComputed, resolved, seed),
          contents: resolveManagerApproveFinancePlaceholders(draft.contents, submissionRecord, pendingComputed, resolved, seed),
          attachments,
          showCc: Boolean(draft.cc),
          showBcc: Boolean(draft.bcc),
        });
      } catch (error) {
        message.error(error?.message ?? 'Unable to prepare approval mail');
      } finally {
        setMailLoading(false);
      }
      return;
    }

    setApprovalSubmitting(true);
    try {
      await approveSubmissionViaVms(submissionId, {
        candidateProposedRate: pendingComputed.proposedRate,
        candidateProposedRateCurrency: pendingComputed.currencyRaw ?? pendingComputed.currency,
        candidateProposedRateUnit: pendingComputed.rateUnit,
        submitVia: 'VMS',
        ...referralPayload(pendingReferral),
      });
      message.success('Submission approved');
      setApprovalChoiceOpen(false);
      setPendingComputed(null);
      setPendingReferral(null);
      onClose?.({ success: true });
    } catch (error) {
      message.error(error?.message ?? 'Unable to approve submission');
    } finally {
      setApprovalSubmitting(false);
    }
  };

  const canSendMail = !mailLoading
    && parseEmails(mail.to).length > 0
    && areValidEmails(mail.to)
    && areValidEmails(mail.cc)
    && areValidEmails(mail.bcc)
    && mail.subject.trim()
    && mail.contents.trim();

  const sendMailAndApprove = async () => {
    if (!pendingComputed || !canSendMail || mailSending) return;
    setMailSending(true);
    const approvalPayload = {
      candidateProposedRate: pendingComputed.proposedRate,
      candidateProposedRateCurrency: pendingComputed.currencyRaw ?? pendingComputed.currency,
      candidateProposedRateUnit: pendingComputed.rateUnit,
    };
    try {
      await approveSubmissionViaVms(submissionId, {
        ...approvalPayload,
        submitVia: 'VMS',
        ...referralPayload(pendingReferral),
      });
      const mailJobId = resolveJobId(resolved?.record, seed);
      if (!mailJobId) {
        message.warning('Submission approved, but job details were not available to send mail');
      } else {
        try {
          await sendBulkMail({
            jobId: mailJobId,
            toMail: parseEmails(mail.to),
            ccMail: parseEmails(mail.cc),
            bccMail: parseEmails(mail.bcc),
            subject: mail.subject,
            contents: mail.contents,
            attachments: mail.attachments ?? [],
          });
          message.success('Approval mail sent and submission approved');
        } catch (mailError) {
          message.warning(mailError?.message
            ? `Submission approved, but mail was not sent: ${mailError.message}`
            : 'Submission approved, but mail was not sent');
        }
      }
      setMailDrawerOpen(false);
      setPendingComputed(null);
      setPendingReferral(null);
      onClose?.({ success: true });
    } catch (error) {
      message.error(error?.message ?? 'Unable to send approval mail');
    } finally {
      setMailSending(false);
    }
  };

  const actions = useMemo(() => ({
    submissionApprove: async ({ computed, referral }) => {
      // Unit and currency are written back AS RESOLVED, never as literals.
      // This used to send `candidateProposedRateUnit: 'hr'`, so approving a
      // daily or monthly rate silently rewrote it to hourly — the recruiter's
      // choice was destroyed by the act of approving it. computed.rateUnit is
      // the engine's resolution (submission → candidate budget → job → config
      // default) and computed.currencyRaw is the currency exactly as stored,
      // so an ISO code ("USD") is not rewritten into a symbol on approve.
      setPendingComputed(computed);
      // Captured here (not read off `computed`) because the referral the
      // Delivery Manager attached lives in the panel's own local state — see
      // referralPayload() above for what actually gets sent.
      setPendingReferral(referral ?? null);
      setApprovalMode('vms');
      setApprovalChoiceOpen(true);
    },
    // Reject hands off to the dedicated Reject popup (reason dropdown +
    // comments) instead of rejecting inline — config marks this reject with
    // `openPopup: 'reject'`. Swap this popup for that one over the SAME page
    // (replace, so Back returns to the page, not here); carry the selection so
    // the reject popup's own api.submit targets this submission.
    submissionReject: async () => {
      context?.navigate?.('/popup/reject', {
        replace: true,
        state: {
          background: context?.background ?? null,
          selectedIds,
          selectedRecords,
          moduleName,
        },
      });
    },
  }), [context, selectedIds, selectedRecords, moduleName]);

  if (loading || !resolved) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><Spin size="large" /></div>;
  }

  return (
    <>
      <FinanceApprovalPanel
        type={FINANCE_TYPE_SUBMISSION_MANAGER}
        record={resolved.record}
        lookups={resolved.lookups}
        identity={buildIdentity(seed, resolved.display)}
        referralOptions={referralOptions}
        currentUserId={typeof localStorage !== 'undefined' ? localStorage.getItem('userId') ?? undefined : undefined}
        actions={actions}
        onClose={() => onClose?.()}
        variant="popup"
      />
      <Modal
        title="Choose approval method"
        open={approvalChoiceOpen}
        okText="Approve"
        confirmLoading={approvalSubmitting}
        onOk={submitApproval}
        onCancel={() => {
          if (approvalSubmitting) return;
          setApprovalChoiceOpen(false);
          setPendingComputed(null);
          setPendingReferral(null);
        }}
      >
        <Radio.Group
          value={approvalMode}
          onChange={(event) => setApprovalMode(event.target.value)}
          style={{ display: 'grid', gap: 12 }}
        >
          <Radio value="vms">Via VMS</Radio>
          <Radio value="mail">Via Mail</Radio>
        </Radio.Group>
      </Modal>
      <Drawer
        open={mailDrawerOpen}
        placement="right"
        onClose={() => {
          if (mailSending) return;
          setMailDrawerOpen(false);
        }}
        closable={false}
        rootClassName="mail-popup"
        width={620}
        maskClosable={false}
        footer={(
          <div className="mail-popup__footer">
            <Button onClick={() => setMailDrawerOpen(false)} disabled={mailSending}>
              Cancel
            </Button>
            <Button type="primary" loading={mailSending} disabled={!canSendMail} onClick={sendMailAndApprove}>
              Send
            </Button>
          </div>
        )}
      >
        {mailLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <Spin size="large" />
          </div>
        ) : (
          <CommonMailComposer
            value={mail}
            onChange={setMail}
            moduleName="submissions"
            ready={!mailLoading}
            title="Submit Via Mail"
            contextRecords={[resolved.record?.submission ?? seed]}
          />
        )}
      </Drawer>
    </>
  );
}

ManagerApproveBody.propTypes = {
  context: PropTypes.object,
  selectedIds: PropTypes.array,
  selectedRecords: PropTypes.array,
  moduleName: PropTypes.string,
  onClose: PropTypes.func,
};
